/*
 * Project: bellcloud
 * File: packages/ui/components/common/Tag.tsx
 * Author: SWAN
 * Created: 2025-01-29
 * Description: 통합 Tag 컴포넌트 - 프로젝트 전체에서 사용하는 표준 태그/배지
 */

import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import type { SerializedStyles } from '@emotion/serialize';

// ==================== Types ====================
interface TagProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** 태그 스타일 variant */
  variant?: 'primary' | 'success' | 'error' | 'warning' | 'info' | 'gradient' | 'custom';
  /** 태그 크기 */
  size?: 'xs' | 'sm' | 'md';
  /** 아이콘 요소 */
  icon?: React.ReactNode;
  /** 태그 너비 */
  width?: string;
  /** 커스텀 배경색 (variant가 'custom'일 때 사용) */
  backgroundColor?: string;
  /** 커스텀 텍스트 색상 (variant가 'custom'일 때 사용) */
  color?: string;
  /** 자식 요소 */
  children?: React.ReactNode;
  /** Emotion CSS prop */
  css?: SerializedStyles;
}

// ==================== Component ====================
export const Tag: React.FC<TagProps> = ({
  variant = 'primary',
  size = 'sm',
  icon,
  width,
  backgroundColor,
  color,
  children,
  ...props
}) => {
  return (
    <StyledTag
      variant={variant}
      size={size}
      width={width}
      backgroundColor={backgroundColor}
      color={color}
      {...props}
    >
      {icon && <IconWrapper>{icon}</IconWrapper>}
      {children}
    </StyledTag>
  );
};

// ==================== Styled Components ====================
const StyledTag = styled('span', {
  shouldForwardProp: prop =>
    prop !== 'variant' &&
    prop !== 'size' &&
    prop !== 'width' &&
    prop !== 'backgroundColor' &&
    prop !== 'color',
})<{
  variant: TagProps['variant'];
  size: TagProps['size'];
  width?: string;
  backgroundColor?: string;
  color?: string;
}>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]}px;
  border-radius: ${({ theme }) => theme.borderRadius.md}px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  white-space: nowrap;
  ${({ width }) =>
    width &&
    css`
      width: ${width};
      min-width: ${width};
      max-width: ${width};
      overflow: hidden;
      text-overflow: ellipsis;
    `}

  /* Size styles */
  ${({ size = 'sm', theme }) => {
    const sizeMap = {
      xs: { height: 20, paddingX: theme.spacing[4], fontSize: theme.fontSize.xs },
      sm: { height: 24, paddingX: theme.spacing[6], fontSize: theme.fontSize.md },
      md: { height: 32, paddingX: theme.spacing[6], fontSize: theme.fontSize.md },
    };
    const config = sizeMap[size];
    return css`
      height: ${config.height}px;
      padding: 0 ${config.paddingX}px;
      font-size: ${config.fontSize}px;
      line-height: ${config.height - 2}px;
    `;
  }}

  /* Variant styles */
  ${({ variant = 'primary', theme, backgroundColor, color }) => {
    if (variant === 'custom' && backgroundColor && color) {
      return css`
        background-color: ${backgroundColor};
        color: ${color};
        border: ${theme.borderWidth.md}px solid ${backgroundColor};
      `;
    }

    switch (variant) {
      case 'primary':
        return css`
          background-color: ${theme.colors.button.bg.primary.default}20;
          color: ${theme.colors.button.bg.primary.default};
          border: ${theme.borderWidth.md}px solid ${theme.colors.button.bg.primary.default};
        `;

      case 'success':
        return css`
          background-color: ${theme.colors.status.stable.fill.surface};
          color: ${theme.colors.status.stable.fill.text};
          border: ${theme.borderWidth.md}px solid ${theme.colors.status.stable.stroke.border};
        `;

      case 'error':
        return css`
          background-color: ${theme.colors.status.danger.fill.surface};
          color: ${theme.colors.status.danger.fill.text};
          border: ${theme.borderWidth.md}px solid ${theme.colors.status.danger.stroke.border};
        `;

      case 'warning':
        return css`
          background-color: ${theme.colors.status.caution.fill.surface};
          color: ${theme.colors.status.caution.fill.text};
          border: ${theme.borderWidth.md}px solid ${theme.colors.status.caution.stroke.border};
        `;

      case 'info':
        return css`
          background-color: ${theme.colors.status.calib.fill.surface};
          color: ${theme.colors.status.calib.fill.text};
          border: ${theme.borderWidth.md}px solid ${theme.colors.status.calib.stroke.border};
        `;

      case 'gradient':
        return css`
          background-color: ${theme.colors.button.bg.primary.default}20;
          color: ${theme.colors.button.bg.primary.default};
          border: ${theme.borderWidth.md}px solid ${theme.colors.button.bg.primary.default};
        `;

      default:
        return css`
          background-color: ${theme.colors.surface.layout.overlay};
          color: ${theme.colors.text.primary};
          border: ${theme.borderWidth.md}px solid ${theme.colors.border.container};
        `;
    }
  }}
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  color: inherit;

  img,
  svg {
    width: 100%;
    height: 100%;
    display: block;
  }
`;
